import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import VerticalSliderLayout from './VerticalSliderLayout';
import { PublicTemplateProvider } from '../../../context/PublicTemplateContext';
// Navigation
import { PublicNavbar } from '../Navigation';

// Import public-aware section components
import Home from '../Home/Home';
import PublicProfileByStyle from '../Profile/PublicProfileByStyle';
import PublicEducationByStyle from '../Education/PublicEducationByStyle';
import PublicCareerByStyle from '../Career/PublicCareerByStyle';
import PublicSkillsByStyle from '../Profile/PublicSkillsByStyle';
import ServicesSection from '../Services/ServicesSection';
import PublicAwardsByStyle from '../Profile/PublicAwardsByStyle';
import TestimonialsSection from '../Testimonials/TestimonialsSection';
import Contact from '../contact';
// Business sections
import { PublicAboutSection } from '../Business';

// Sections available per website type
const SECTIONS_BY_TYPE = {
  portfolio: ['home', 'profile', 'education', 'career', 'skills', 'services', 'awards', 'testimonials', 'contact', 'footer'],
  business: ['home', 'about', 'services', 'how_it_works', 'why_choose_us', 'stats', 'legacy', 'testimonials', 'gallery', 'faq', 'team', 'clients', 'locations', 'contact', 'footer'],
  freelancer: ['home', 'profile', 'services', 'how_it_works', 'skills', 'testimonials', 'clients', 'pricing', 'faq', 'contact', 'footer'],
};

// Section labels
const SECTION_CONFIG = {
  home: { label: 'Home', settingKey: 'show_home' },
  profile: { label: 'About Me', settingKey: 'show_profile' },
  about: { label: 'About Us', settingKey: 'show_about' },
  education: { label: 'Education', settingKey: 'show_education' },
  career: { label: 'Experience', settingKey: 'show_career' },
  skills: { label: 'Skills', settingKey: 'show_skills' },
  services: { label: 'Services', settingKey: 'show_services' },
  how_it_works: { label: 'How It Works', settingKey: 'show_how_it_works' },
  why_choose_us: { label: 'Why Choose Us', settingKey: 'show_why_choose_us' },
  stats: { label: 'Stats', settingKey: 'show_stats' },
  legacy: { label: 'Our Legacy', settingKey: 'show_legacy' },
  awards: { label: 'Awards', settingKey: 'show_awards' },
  testimonials: { label: 'Testimonials', settingKey: 'show_testimonials' },
  gallery: { label: 'Gallery', settingKey: 'show_gallery' },
  faq: { label: 'FAQ', settingKey: 'show_faq' },
  team: { label: 'Team', settingKey: 'show_team' },
  clients: { label: 'Clients', settingKey: 'show_clients' },
  locations: { label: 'Locations', settingKey: 'show_locations' },
  pricing: { label: 'Pricing', settingKey: 'show_pricing' },
  contact: { label: 'Contact', settingKey: 'show_contact' },
  footer: { label: 'Connect', settingKey: null },
};

// Placeholder for business sections not yet implemented
const PlaceholderSection = ({ title }) => (
  <div style={{ padding: '4rem 2rem', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
    <h2 style={{ color: '#fff', fontSize: '2rem', marginBottom: '1rem' }}>{title}</h2>
    <p style={{ color: 'rgba(255,255,255,0.6)' }}>Coming soon</p>
  </div>
);

// Component that renders sections INSIDE the provider context
const VerticalSliderLayoutWithSections = ({ sections, accentColor, username }) => {
  // This component is rendered inside PublicTemplateProvider
  // so all child components will have access to the context
  const getSectionComponent = (key) => {
    switch (key) {
      // Portfolio sections
      case 'home': return <Home />;
      case 'profile': return <PublicProfileByStyle />;
      case 'education': return <PublicEducationByStyle />;
      case 'career': return <PublicCareerByStyle />;
      case 'skills': return <PublicSkillsByStyle />;
      case 'services': return <ServicesSection />;
      case 'awards': return <PublicAwardsByStyle />;
      case 'testimonials': return <TestimonialsSection />;
      case 'contact': return <Contact />;
      case 'footer': return <FooterSection username={username} accentColor={accentColor} />;
      // Business sections
      case 'about': return <PublicAboutSection />;
      case 'how_it_works': return <PlaceholderSection title="How It Works" />;
      case 'why_choose_us': return <PlaceholderSection title="Why Choose Us" />;
      case 'stats': return <PlaceholderSection title="Our Numbers" />;
      case 'legacy': return <PlaceholderSection title="Our Legacy" />;
      case 'gallery': return <PlaceholderSection title="Gallery" />;
      case 'faq': return <PlaceholderSection title="FAQ" />;
      case 'team': return <PlaceholderSection title="Our Team" />;
      case 'clients': return <PlaceholderSection title="Our Clients" />;
      case 'locations': return <PlaceholderSection title="Our Locations" />;
      case 'pricing': return <PlaceholderSection title="Pricing" />;
      default: return null;
    }
  };

  const sectionsWithComponents = sections.map(section => ({
    ...section,
    component: getSectionComponent(section.key),
  }));

  return (
    <VerticalSliderLayout
      sections={sectionsWithComponents}
      accentColor={accentColor}
    />
  );
};

// Footer Section Component
const FooterSection = ({ username, accentColor }) => {
  const [profile, setProfile] = useState({});

  useEffect(() => {
    if (username) {
      api.get(`/public/profile/${username}/`)
        .then(res => {
          setProfile({
            name: res.data?.homeContent?.title || res.data?.userProfile?.full_name || username,
            email: res.data?.userProfile?.email || res.data?.email,
            phone: res.data?.userProfile?.phone || res.data?.phone,
            linkedin: res.data?.linkedinLink || res.data?.linkedin_link,
            twitter: res.data?.twitterLink || res.data?.twitter_link,
            github: res.data?.githubLink || res.data?.github_link,
            instagram: res.data?.instagramLink || res.data?.instagram_link,
          });
        })
        .catch(err => console.error('Error fetching profile:', err));
    }
  }, [username]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      paddingBottom: '5rem',
      color: '#fff',
    }}>
      <div style={{ textAlign: 'center', maxWidth: '600px' }}>
        <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
          Let's Connect
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', fontSize: '1.1rem' }}>
          Thank you for viewing my portfolio. Feel free to reach out!
        </p>

        {/* Contact Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
          {profile.email && (
            <a href={`mailto:${profile.email}`} style={{ color: accentColor, textDecoration: 'none', fontSize: '1.1rem' }}>
              {profile.email}
            </a>
          )}
          {profile.phone && (
            <a href={`tel:${profile.phone}`} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>
              {profile.phone}
            </a>
          )}
        </div>

        {/* Social Links */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          {profile.linkedin && (
            <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" style={{
              width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </a>
          )}
          {profile.github && (
            <a href={profile.github} target="_blank" rel="noopener noreferrer" style={{
              width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
            </a>
          )}
          {profile.twitter && (
            <a href={profile.twitter} target="_blank" rel="noopener noreferrer" style={{
              width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
            </a>
          )}
          {profile.instagram && (
            <a href={profile.instagram} target="_blank" rel="noopener noreferrer" style={{
              width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const VerticalProfilePage = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  // Also check cached custom domain username (resolved in App.js)
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [settings, setSettings] = useState(null);
  const [websiteType, setWebsiteType] = useState('portfolio');
  const [loading, setLoading] = useState(true);
  const [accentColor, setAccentColor] = useState('#6366f1');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (username) {
          const res = await api.get(`/public/settings/${username}/`);
          setSettings(res.data?.settings || {});

          // Get website type from settings
          const wType = res.data?.website_type || res.data?.settings?.website_type || 'portfolio';
          setWebsiteType(wType);

          // Try to get accent color from template
          const templateRes = await api.get(`/templates/public/${username}/`).catch(() => null);
          if (templateRes?.data?.config?.global?.accentColor) {
            setAccentColor(templateRes.data.config.global.accentColor);
          }
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [username]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: '3rem',
          height: '3rem',
          border: '4px solid rgba(255,255,255,0.1)',
          borderTopColor: accentColor,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  // Build sections based on website type
  const allSections = (SECTIONS_BY_TYPE[websiteType] || SECTIONS_BY_TYPE.portfolio)
    .map(key => ({
      key,
      label: SECTION_CONFIG[key]?.label || key,
      settingKey: SECTION_CONFIG[key]?.settingKey || `show_${key}`,
    }));

  // Filter based on settings (default to show if no setting)
  const visibleSections = allSections.filter(section => {
    if (section.settingKey === null) return true;
    if (!settings) return true;
    return settings[section.settingKey] !== false;
  });

  return (
    <PublicTemplateProvider>
      <VerticalSliderLayoutWithSections
        sections={visibleSections}
        accentColor={accentColor}
        username={username}
      />
    </PublicTemplateProvider>
  );
};

export default VerticalProfilePage;
